import { FC, useEffect, useMemo, useState, useCallback } from 'react';
import {
	Modal,
	Row,
	Table,
	InputNumber,
	Button,
	Space,
	TreeSelect,
	Switch,
	Upload,
	Progress,
	Spin,
	Tooltip,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import './index.scss';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import {
	DeleteOutlined,
	UploadOutlined,
	DownloadOutlined,
	QuestionCircleOutlined,
} from '@ant-design/icons';
import {
	AllocationCsvRow,
	isPercentageTotalValid,
	parseAllocationCsv,
} from 'utils/allocationCsv';
import {
	upsertExpenseGroupAllocationsBatchAction,
	importExpenseGroupAllocationCsvAction,
} from 'redux/action/expenseGroupAllocationAction';
// import { getExpenseGroupsAction } from 'redux/action/expenseGroupAction';
import {
	setSingleAccountMode,
	setTemporaryConfig,
	setAllocations,
} from 'redux/slice/expenseGroupAllocationSlice';
import { getApiCSV } from 'redux/apis';
import { toastText } from 'utils/utils';
import { BlueAddSvg } from 'utils/svgs';

interface Props {
	isModalOpen: boolean;
	handleCancel: () => void;
	expenseGroup: {
		id: string;
		name: string;
		description?: string;
		accounts?: string[];
	} | null;
	onAllocationSaved?: () => void;
	isTemporaryMode?: boolean; // If true, store changes temporarily instead of saving immediately
}
interface AllocationRow {
	id: string;
	customer?: string; // value (id)
	class?: string; // value (id)
	location?: string; // value (id)
	percentage?: number;
}

const ExpenseGroupSettingsModal: FC<Props> = ({
	isModalOpen,
	handleCancel,
	expenseGroup,
	onAllocationSaved,
	isTemporaryMode = false,
}) => {
	const dispatch = useDispatch<AppDispatch>();

	// Redux option lists
	const { optionData: qbClass } = useSelector((state: any) => state.class);
	const { data: qbChartOfAccounts } = useSelector(
		(state: any) => state.chartOfAccounts
	);
	const { optionData: qbCustomers } = useSelector(
		(state: any) => state.customer
	);
	const { optionData: qbLocations } = useSelector(
		(state: any) => state.locations
	);
	const { isLoading, currentItems, accounts, temporaryConfig } = useSelector(
		(state: any) => state.expenseGroupAllocation
	);

	const { data: expenseGroups } = useSelector(
		(state: any) => state.expenseGroups
	);

	// Get expense group data from Redux and set allocations
	useEffect(() => {
		if (isModalOpen && expenseGroup?.id) {
			// Clear any existing CSV errors when opening modal
			setCsvErrors([]);
			// Reset selected accounts while fresh data loads
			setSelectedAccounts([]);
			// Reset rows to a clean state for fresh expense group
			setRows([{ id: 'r1', percentage: 0 }]);

			// Get expense group data from Redux state
			const selectedExpenseGroup = expenseGroups?.find(
				(group: any) => group.id === expenseGroup.id
			);


			if (selectedExpenseGroup) {
				const allocationsToSet = selectedExpenseGroup.expenseAllocations || [];
				const accountsToSet = selectedExpenseGroup.accounts || [];

				// Set allocations directly from expense group data
				dispatch(
					setAllocations({
						allocations: allocationsToSet,
						accounts: accountsToSet,
					})
				);

				// Set switch state from expense group's isSingleAccountAllocation
				const singleAccountValue =
					selectedExpenseGroup.isSingleAccountAllocation ?? false;
				setSingleAccountOnly(singleAccountValue);

				// If switch is ON, only set accounts if configAccounts exist from backend
				if (singleAccountValue) {
					const configAccounts = selectedExpenseGroup.configAccounts || [];
					if (configAccounts && configAccounts.length > 0) {
						setSelectedAccounts(configAccounts);
					} else {
						// No configAccounts from backend, so no account selected
						setSelectedAccounts([]);
					}
				}
			} else {
				console.log('WARNING: Selected expense group not found in Redux!');
			}
		}
		console.log('=== MODAL OPEN useEffect END ===');
	}, [isModalOpen, expenseGroup?.id, dispatch, expenseGroups]);

	const transformToTreeData = (data: any[]): any[] => {
		if (!Array.isArray(data)) {
			return [];
		}

		return (data || [])
			.filter((item: any) => item && item.Id)
			.map((item: any) => ({
				title:
					item.title ||
					item.Name ||
					item.DisplayName ||
					item.FullyQualifiedName,
				value: item.Id,
				key: item.Id,
				disabled: item.disabled === true,
				children: item.children && Array.isArray(item.children) && item.children.length > 0
					? transformToTreeData(item.children)
					: [],
			}));
	};

	const treeData = useMemo(
		() => ({
			accounts: transformToTreeData(qbChartOfAccounts || []),
			customers: transformToTreeData(qbCustomers || []),
			classes: transformToTreeData(qbClass || []),
			locations: transformToTreeData(qbLocations || []),
		}),
		[qbChartOfAccounts, qbCustomers, qbClass, qbLocations]
	);

	// No filtering of accounts list; we only preselect using accounts

	// Helpers to validate/match (recursive for nested children)
	const flattenTree = (
		nodes: any[]
	): Array<{ title: string; value: string }> => {
		const out: Array<{ title: string; value: string }> = [];
		const flatten = (items: any[]) => {
			items?.forEach((item: any) => {
				out.push({ title: item.title, value: item.value });
				if (item.children && Array.isArray(item.children) && item.children.length > 0) {
					flatten(item.children);
				}
			});
		};
		flatten(nodes);
		return out;
	};
	const customerIndex = useMemo(
		() => flattenTree(treeData.customers),
		[treeData.customers]
	);
	const classIndex = useMemo(
		() => flattenTree(treeData.classes),
		[treeData.classes]
	);
	const locationIndex = useMemo(
		() => flattenTree(treeData.locations),
		[treeData.locations]
	);
	const findValueByTitle = (
		title?: string,
		list?: Array<{ title: string; value: string }>
	) => {
		if (!title || !list) return undefined;
		// Find the option with the matching title (case-insensitive) and return its value
		const foundOption = list.find(
			(option) =>
				option.title.toLowerCase().trim() === title.toLowerCase().trim()
		);
		return foundOption?.value;
	};
	const findTitleByValue = (
		value?: string,
		list?: Array<{ title: string; value: string }>
	) => {
		if (!value || !list) return undefined;
		// Find the option with the matching value and return its title
		const foundOption = list.find((option) => option.value === value);
		return foundOption?.title;
	};

	// Helper function to render account selection with tooltip
	const renderAccountSelection = () => {
		// Only show dropdown when switch is ON
		if (!singleAccountOnly) {
			return null;
		}

		return (
			<div className={styles.accountRow}>
				<TreeSelect
					showSearch
					allowClear
					treeDefaultExpandAll
					filterTreeNode={(inputValue, treeNode) =>
						(treeNode.props?.title || treeNode.title || '')
							.toLowerCase()
							.includes((inputValue || '').toLowerCase())
					}
					treeData={treeData.accounts}
					treeLine
					placeholder="Select account"
					style={{ width: '100%' }}
					className={styles.customTreeSelect}
					size="large"
					value={selectedAccounts[0] ?? undefined}
					onChange={(val) => {
						const v = (val as string) || undefined;
						setSelectedAccounts(v ? [v] : []);
					}}
				/>

				{/* Account validation logic moved here */}
				{singleAccountOnly && selectedAccounts.length === 0 && (
					<div
						style={{
							color: '#cf1322',
							fontSize: 12,
							marginTop: 10,
							marginLeft: 10,
						}}
					>
						At least one account is required when single account mode is enabled
					</div>
				)}
			</div>
		);
	};

	const [selectedAccounts, setSelectedAccounts] = useState<string[]>(
		expenseGroup?.accounts || []
	);

	const [singleAccountOnly, setSingleAccountOnly] = useState<boolean>(false);

	// Generate unique IDs using timestamp + random number to avoid collisions
	const generateUniqueId = useCallback(
		() => `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
		[]
	);

	const [rows, setRows] = useState<AllocationRow[]>([
		{ id: generateUniqueId(), percentage: 0 },
	]);

	// Map fetched items to rows + accounts
	useEffect(() => {
		// If in temporary mode and temporary config exists for this expense group, use it
		if (
			isTemporaryMode &&
			temporaryConfig &&
			temporaryConfig.expenseGroupId === expenseGroup?.id
		) {
			// Restore from temporary config
			const tempAccounts = temporaryConfig.configAccounts || [];
			setSelectedAccounts(tempAccounts);
			if (temporaryConfig.singleAccountMode !== undefined) {
				setSingleAccountOnly(temporaryConfig.singleAccountMode);
			}

			// Check if temporaryConfig has meaningful allocation items
			// (has items with at least one non-zero percentage or valid customer/class/location)
			const hasValidTemporaryItems =
				temporaryConfig.items &&
				temporaryConfig.items.length > 0 &&
				temporaryConfig.items.some(
					(item: any) =>
						Number(item.percentage) > 0 ||
						item.customerId ||
						item.classId ||
						item.locationId
				);

			// If temporaryConfig has valid items, use them
			// Otherwise, fall back to original allocations from backend to preserve them
			// This ensures original customer/class/location allocations are preserved when only accounts change
			if (hasValidTemporaryItems) {
				const mapped: AllocationRow[] = (temporaryConfig.items || []).map(
					(item: any) => ({
						id: generateUniqueId(),
						customer: item.customerId,
						class: item.classId,
						location: item.locationId,
						percentage: Number(item.percentage) || 0,
					})
				);
				setRows(
					mapped.length ? mapped : [{ id: generateUniqueId(), percentage: 0 }]
				);
				return;
			}
			// If temporaryConfig doesn't have valid items, fall through to use original allocations
			// But keep the accounts from temporaryConfig (user may have changed them)
			// So we skip the account syncing below and just map the original allocation items
			// Note: Accounts are already set above from temporaryConfig.configAccounts
			const mapped: AllocationRow[] = (currentItems || []).map((item: any) => ({
				id: generateUniqueId(),
				customer:
					item.customerId || findValueByTitle(item.customerName, customerIndex),
				class: item.classId || findValueByTitle(item.className, classIndex),
				location:
					item.locationId || findValueByTitle(item.locationName, locationIndex),
				percentage: Number(item.percentage) || 0,
			}));
			setRows(
				mapped.length ? mapped : [{ id: generateUniqueId(), percentage: 0 }]
			);
			return;
		} else {
			// No temporaryConfig or different expense group - sync from backend normally
			// When temporaryConfig is cleared (e.g., on tab change), reset to expenseGroup.accounts or Redux accounts
			// This ensures that when tabs change and temporaryConfig is cleared, accounts reset to their original values

			// Restore singleAccountOnly state from expense group data
			// This applies to both temporary and non-temporary modes
			let restoredSingleMode = false;
			let selectedExpenseGroup: any = null;

			if (expenseGroup?.id) {
				// Get expense group data from Redux state
				selectedExpenseGroup = expenseGroups?.find(
					(group: any) => group.id === expenseGroup.id
				);


				if (selectedExpenseGroup?.isSingleAccountAllocation !== undefined) {
					restoredSingleMode = selectedExpenseGroup.isSingleAccountAllocation;
					setSingleAccountOnly(selectedExpenseGroup.isSingleAccountAllocation);
				} else {
					// If isSingleAccountAllocation is not defined, default to false
					setSingleAccountOnly(false);
				}
			}

			// Simple account selection logic:
			// - If isSingleAccountAllocation is true (switch is ON) → use configAccounts only if they exist from backend
			// - If isSingleAccountAllocation is false (switch is OFF) → use Redux expenseGroup.accounts
			const configAccounts = selectedExpenseGroup?.configAccounts || [];
			const expenseGroupAccounts = selectedExpenseGroup?.accounts || [];

			// When switch is ON: only use configAccounts if they exist and are not empty
			// When switch is OFF: use expenseGroupAccounts
			const syncedAccounts = restoredSingleMode
				? (configAccounts && configAccounts.length > 0 ? configAccounts : [])
				: expenseGroupAccounts;

			setSelectedAccounts(syncedAccounts);
		}

		const mapped: AllocationRow[] = (currentItems || []).map((item: any) => ({
			id: generateUniqueId(),
			customer:
				item.customerId || findValueByTitle(item.customerName, customerIndex),
			class: item.classId || findValueByTitle(item.className, classIndex),
			location:
				item.locationId || findValueByTitle(item.locationName, locationIndex),
			percentage: Number(item.percentage) || 0,
		}));
		setRows(
			mapped.length ? mapped : [{ id: generateUniqueId(), percentage: 0 }]
		);

	}, [
		currentItems,
		accounts,
		customerIndex,
		classIndex,
		locationIndex,
		expenseGroup?.id,
		// Include expenseGroup.accounts in dependencies, but logic handles whether to use it
		// In temporary mode, we ignore prop accounts and use Redux accounts
		expenseGroup?.accounts,
		expenseGroups,
		isTemporaryMode,
		temporaryConfig,
	]);

	const [validationError, setValidationError] = useState<string>('');
	const [csvErrors, setCsvErrors] = useState<string[]>([]);
	const [sampleLoading, setSampleLoading] = useState<boolean>(false);
	const [proceedLoading, setProceedLoading] = useState<boolean>(false);

	useEffect(() => {
		const total = rows.reduce((sum, r) => sum + (Number(r.percentage) || 0), 0);
		const validationErrors: string[] = [];

		// Only check total percentage - must equal 100 (±0.01, same as the backend)
		if (rows.length && !isPercentageTotalValid(total)) {
			validationErrors.push('Total percentage must equal 100.');
		}

		// Account validation is now handled inline in the account selection component

		setValidationError(validationErrors.join('; '));
	}, [rows, selectedAccounts]);

	const addRow = () => {
		setRows((prev) => [...prev, { id: generateUniqueId(), percentage: 0 }]);
		// Clear CSV errors when adding new rows
		setCsvErrors([]);
	};

	const removeRow = (id: string) => {
		setRows((prev) => prev.filter((r) => r.id !== id));
		// Clear CSV errors when removing rows
		setCsvErrors([]);
	};

	const updateRow = (id: string, patch: Partial<AllocationRow>) => {
		setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
		// Clear CSV errors when user manually updates rows
		setCsvErrors([]);
	};

	// CSV
	const [uploading, setUploading] = useState(false);
	const [uploadProgress, setUploadProgress] = useState<number>(0);
	const [importLoading, setImportLoading] = useState(false);

	const handleCsvParsed = (parsedRows: AllocationCsvRow[]) => {
		setRows(
			parsedRows.length
				? parsedRows.map((row) => ({ id: generateUniqueId(), ...row }))
				: [{ id: generateUniqueId(), percentage: undefined }]
		);
	};

	const beforeUpload = async (file: File) => {
		// Check file size limit (10MB = 10 * 1024 * 1024 bytes)
		const maxFileSize = 10 * 1024 * 1024; // 10MB in bytes
		if (file.size > maxFileSize) {
			toastText(
				'File size exceeds 10MB limit. Please select a smaller file.',
				'error'
			);
			return false;
		}

		setUploading(true);
		setImportLoading(true);
		setUploadProgress(0);
		setCsvErrors([]);

		try {
			const text = await file.text();
			// Parse CSV data and validate headers, percentages and names
			const { rows: mappedRows, errors: newErrors } = await parseAllocationCsv(
				text,
				{ customers: customerIndex, classes: classIndex, locations: locationIndex }
			);

			setCsvErrors(newErrors);

			// If there are validation errors, don't proceed with API call
			if (newErrors.length > 0) {
				setUploadProgress(100);
				setTimeout(() => {
					setUploading(false);
					setImportLoading(false);
				}, 300);
				return false;
			}

			// Check if accounts are selected before importing (only when single account mode is enabled)
			// if (singleAccountOnly && selectedAccounts.length === 0) {
			// 	newErrors.push(
			// 		'Please select at least one account before importing CSV when single account mode is enabled'
			// 	);
			// 	setCsvErrors(newErrors);
			// 	setUploadProgress(100);
			// 	setTimeout(() => {
			// 		setUploading(false);
			// 		setImportLoading(false);
			// 	}, 300);
			// 	return false;
			// }

			// Determine accounts to use for import
			// If switch is OFF, use expenseGroup.accounts; if ON, use selectedAccounts
			const accountsForImport = singleAccountOnly && selectedAccounts.length > 0
				? selectedAccounts
				: (expenseGroup?.accounts || []);

			// Call the API to import the data
			if (expenseGroup?.id) {
				setUploadProgress(50);
				const importResult = await dispatch(
					importExpenseGroupAllocationCsvAction({
						expenseGroupId: expenseGroup.id,
						accounts: accountsForImport,
						rows: mappedRows,
					})
				).unwrap();

				setUploadProgress(75);

				// If validation was successful, populate the UI table with validated data
				if (
					importResult?.validatedData &&
					importResult.validatedData.length > 0
				) {
					// Transform validated data to the format expected by the table
					const validatedRows: AllocationRow[] = importResult.validatedData.map(
						(item: any) => ({
							id: generateUniqueId(),
							customer: item.customerId,
							class: item.classId,
							location: item.locationId,
							percentage: Number(item.percentage) || 0,
						})
					);

					// Update the table with validated data
					setRows(validatedRows);
				}

				setUploadProgress(100);
				toastText(
					`CSV imported successfully. ${importResult?.count || 0
					} allocations validated.`,
					'success'
				);
			} else {
				// If no expense group, just parse and fill rows
				handleCsvParsed(mappedRows);
				setUploadProgress(100);
				toastText('CSV parsed successfully', 'success');
			}
		} catch (e: any) {
			console.error('CSV import failed', e);
			// Keep the reason on screen; a toast alone leaves only the stale total message behind
			setCsvErrors([e?.message || 'Failed to import CSV']);
			toastText(e?.message || 'Failed to import CSV', 'error');
		} finally {
			setTimeout(() => {
				setUploading(false);
				setImportLoading(false);
			}, 300);
		}
		return false;
	};

	const handleDownloadSample = async () => {
		try {
			setSampleLoading(true);
			const companyId = localStorage.getItem('companyId');
			const res = await getApiCSV(
				'/configure-allocations/allocations/sample-export',
				{ companyId }
			);
			const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', 'sample_allocation.csv');
			document.body.appendChild(link);
			link.click();
			link.parentNode?.removeChild(link);
			window.URL.revokeObjectURL(url);
			toastText('Sample CSV downloaded', 'success');
		} catch (e: any) {
			console.error('Sample export failed', e);
			toastText('Failed to download sample CSV', 'error');
		} finally {
			setSampleLoading(false);
		}
	};

	const handleProceed = () => {

		if (
			validationError ||
			csvErrors.length > 0 ||
			(singleAccountOnly && selectedAccounts.length === 0)
		) {

			return;
		}

		if (!expenseGroup?.id) {

			return handleCancel();
		}

		setProceedLoading(true);

		// Map rows to items first, filtering out 0% entries
		const mappedItems = rows
			.map((r) => {
				const item = {
					percentage: Number(r.percentage) || 0,
					customerId: r.customer,
					customerName: findTitleByValue(r.customer, customerIndex),
					classId: r.class,
					className: findTitleByValue(r.class, classIndex),
					locationId: r.location,
					locationName: findTitleByValue(r.location, locationIndex),
				};
				return item;
			})
			.filter((item) => Number(item.percentage) > 0); // Discard 0% entries

		// Group and sum items with the same customer, class, and location combination
		const itemsMap = new Map<string, typeof mappedItems[0]>();

		mappedItems.forEach((item) => {
			// Create a unique key from customer, class, and location IDs
			const key = `${item.customerId || ''}-${item.classId || ''}-${item.locationId || ''}`;

			if (itemsMap.has(key)) {
				// Sum up percentages for duplicate combinations
				const existingItem = itemsMap.get(key)!;
				existingItem.percentage = (Number(existingItem.percentage) || 0) + (Number(item.percentage) || 0);
			} else {
				// First occurrence of this combination
				itemsMap.set(key, { ...item });
			}
		});

		// Convert map back to array and filter out any items that ended up with 0% after summing
		const items = Array.from(itemsMap.values()).filter(
			(item) => Number(item.percentage) > 0
		);

		// Prepare payload based on API requirements
		// If switch is ON: send configAccounts (required, non-empty)
		// If switch is OFF: send accounts (required, can be empty array)
		let temporaryConfigPayload: any;
		let upsertPayload: any;

		if (singleAccountOnly) {
			// Switch is ON: requires configAccounts (non-empty array)
			if (selectedAccounts.length === 0) {
				// Validation should have caught this, but double-check
				return;
			}
			const configAccountsToSave = [selectedAccounts[0]];

			// Temporary mode payload
			temporaryConfigPayload = {
				expenseGroupId: expenseGroup.id,
				configAccounts: configAccountsToSave,
				items,
				singleAccountMode: true,
			};

			// Backend payload - only send configAccounts, not accounts
			upsertPayload = {
				expenseGroupId: expenseGroup.id,
				configAccounts: configAccountsToSave,
				items,
				isSingleAccountAllocation: true,
			};
		} else {
			// Switch is OFF: requires accounts (can be empty array)
			const accountsToSave = expenseGroup?.accounts || [];

			// Temporary mode payload
			temporaryConfigPayload = {
				expenseGroupId: expenseGroup.id,
				configAccounts: accountsToSave, // Store in configAccounts for temp mode
				items,
				singleAccountMode: false,
			};

			// Backend payload - only send accounts, not configAccounts
			upsertPayload = {
				expenseGroupId: expenseGroup.id,
				accounts: accountsToSave,
				items,
				isSingleAccountAllocation: false,
			};
		}

		// If in temporary mode, store temporarily instead of saving to backend
		if (isTemporaryMode) {
			dispatch(setTemporaryConfig(temporaryConfigPayload));

			// Also save single account mode state to Redux so it persists across modals
			// This ensures settings page modal and temporary mode modal share the same toggle state
			const singleAccountModePayload = {
				expenseGroupId: expenseGroup.id,
				value: singleAccountOnly,
			};

			dispatch(setSingleAccountMode(singleAccountModePayload));
			toastText('Configuration saved', 'success');
			onAllocationSaved?.(); // Call callback to refresh preview if needed
			setProceedLoading(false);
			handleCancel();
			return;
		}

		// Normal mode: save immediately to backend

		dispatch(upsertExpenseGroupAllocationsBatchAction(upsertPayload))
			.unwrap()
			.then(async (res: any) => {
				toastText(res?.message || 'Allocations saved', 'success');
				onAllocationSaved?.();
				handleCancel();
			})
			.catch((err: any) => {
				toastText(err?.message || 'Failed to save allocations', 'error');
			})
			.finally(() => {
				setProceedLoading(false);
			});
	};

	const handleReset = () => {
		setSelectedAccounts([]);
		setRows([{ id: generateUniqueId(), percentage: undefined }]);
		setValidationError('');
		setCsvErrors([]);
	};

	const columns: any[] = [
		{
			title: 'Customer',
			key: 'customer',
			dataIndex: 'customer',
			width: 200,
			render: (_: any, record: AllocationRow) => (
				<TreeSelect
					showSearch
					allowClear
					treeDefaultExpandAll
					filterTreeNode={(inputValue, treeNode) =>
						(treeNode.props?.title || treeNode.title || '')
							.toLowerCase()
							.includes((inputValue || '').toLowerCase())
					}
					value={record.customer}
					treeData={treeData.customers}
					placeholder={'Select Customer'}
					onChange={(val) => updateRow(record.id, { customer: val as string })}
					style={{ width: '100%' }}
					size="large"
					treeLine
					className={styles.customTreeSelect}
				/>
			),
		},
		{
			title: 'Class',
			key: 'class',
			dataIndex: 'class',
			width: 180,
			render: (_: any, record: AllocationRow) => (
				<TreeSelect
					showSearch
					allowClear
					treeDefaultExpandAll
					filterTreeNode={(inputValue, treeNode) =>
						(treeNode.props?.title || treeNode.title || '')
							.toLowerCase()
							.includes((inputValue || '').toLowerCase())
					}
					value={record.class}
					treeData={treeData.classes}
					placeholder={'Select Class'}
					onChange={(val) => updateRow(record.id, { class: val as string })}
					style={{ width: '100%' }}
					size="large"
					treeLine
					className={styles.customTreeSelect}
				/>
			),
		},
		{
			title: 'Location',
			key: 'location',
			dataIndex: 'location',
			width: 180,
			render: (_: any, record: AllocationRow) => (
				<TreeSelect
					showSearch
					allowClear
					treeDefaultExpandAll
					filterTreeNode={(inputValue, treeNode) =>
						(treeNode.props?.title || treeNode.title || '')
							.toLowerCase()
							.includes((inputValue || '').toLowerCase())
					}
					value={record.location}
					treeData={treeData.locations}
					placeholder={'Select Location'}
					onChange={(val) => updateRow(record.id, { location: val as string })}
					style={{ width: '100%' }}
					size="large"
					treeLine
					className={styles.customTreeSelect}
				/>
			),
		},
		{
			title: 'Percentage',
			key: 'percentage',
			dataIndex: 'percentage',
			width: 120,
			// align: 'right' as const,
			render: (_: any, record: AllocationRow) => (
				<InputNumber
					min={0}
					max={100}
					addonAfter="%"
					value={record.percentage}
					onChange={(val) =>
						updateRow(record.id, { percentage: (val as number) ?? undefined })
					}
					size="large"
					style={{ width: '100%' }}
					className={styles.percentInput}
				/>
			),
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 100,
			align: 'center' as const,
			render: (_: any, record: AllocationRow) => (
				<Space size={8}>
					{/* <Button
						type="text"
						onClick={addRow}
						className={styles.actionButton}
						size="small"
					>
						+
					</Button> */}
					{rows.length > 1 && (
						<Button
							type="text"
							danger
							icon={<DeleteOutlined />}
							onClick={() => removeRow(record.id)}
							className={styles.actionButton}
							size="small"
						></Button>
					)}
				</Space>
			),
		},
	];

	return (
		<>
			<Modal
				open={isModalOpen}
				onCancel={handleCancel}
				className="custom-modal"
				closable={true}
				footer={false}
				width={1200}
				maskClosable={false}
			>
				<Spin
					spinning={importLoading || (isLoading && !proceedLoading)}
					indicator={
						<LoadingOutlined style={{ fontSize: 24, color: '#344735' }} spin />
					}
					size="large"
				>
					<Row className={styles['modal__header']}>
						<h4 className={styles['modal__title']}>
							Configure Allocation -{' '}
							<span className={styles['title-name']}>{expenseGroup?.name}</span>
						</h4>
					</Row>

					<div className={styles['modal__body']}>
						<div className={styles.section}>
							<div
								className={styles.sectionHeader}
								style={{
									display: 'flex',
									justifyContent: 'flex-start',
									alignItems: 'center',
									gap: 24,
								}}
							>
								<span>Account Selection</span>
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: 8,
										whiteSpace: 'nowrap',
									}}
								>
									<span
										style={{
											fontSize: '12px',
											color: '#344735',
											fontWeight: 600,
										}}
									>
										Single Account
									</span>
									<Switch
										checked={singleAccountOnly}
										onChange={(checked) => {
											setSingleAccountOnly(checked);
											if (checked) {
												// When switching ON: only set account if configAccounts exist from backend
												const selectedExpenseGroup = expenseGroups?.find(
													(group: any) => group.id === expenseGroup?.id
												);
												const configAccounts = selectedExpenseGroup?.configAccounts || [];
												// Only set if configAccounts exist and are not empty
												if (configAccounts && configAccounts.length > 0) {
													setSelectedAccounts(configAccounts);
												} else {
													// No configAccounts from backend, so no account selected
													setSelectedAccounts([]);
												}
											} else {
												// When switching OFF, clear selected accounts since we'll use expenseGroup.accounts
												setSelectedAccounts([]);
											}
										}}
									/>
									<Tooltip
										title={
											<div style={{ fontSize: '12px', lineHeight: '1.6' }}>
												<div style={{ marginBottom: '8px' }}>
													<strong>When turned off:</strong> Allocations debit and credit the original expense accounts in your filtered transactions.
												</div>
												<div>
													<strong>When turned on:</strong> All filtered transactions are summed and posted to the single account you select here. Debits follow the allocation breakdown you define, while credits return to the same single account using the original customers, classes, and locations.
												</div>
											</div>
										}
										placement="right"
										overlayStyle={{ zIndex: 10000 }}
										overlayInnerStyle={{ width: '520px',maxWidth: '600px', padding: '12px' }}
									>
										<QuestionCircleOutlined
											style={{
												fontSize: '20px',
												color: '#999',
												cursor: 'help',
												marginLeft: '2px',
											}}
										/>
									</Tooltip>
								</div>
							</div>
							{renderAccountSelection()}
						</div>

						<div className={styles.section}>
							<div className={styles.sectionHeader}>
								Allocation Breakdown for{' '}
								<span className={styles['section-name']}>
									{expenseGroup?.name}
								</span>
							</div>
							<div className={styles.tableHeaderActions}>
								<Space size={8}>
									{/* <Button
								size="small"
								onClick={() => setIsPreviewOpen(true)}
								className={styles.headerButtons}
								style={{
									backgroundColor: '#344735',
									fontSize: '12px',
									borderRadius: '12px',
									height: '41px',
									display: 'inline-flex',
									alignItems: 'center',
									justifyContent: 'center',
									color: 'white',
									width: '13rem',
								}}
							>
								Preview Journal
							</Button> */}
									<Button
										size="small"
										icon={<UploadOutlined />}
										loading={sampleLoading}
										onClick={handleDownloadSample}
										className={styles.headerButtons}
										style={{
											backgroundColor: '#344735',
											fontSize: '12px',
											borderRadius: '12px',
											height: '41px',
											display: 'inline-flex',
											alignItems: 'center',
											justifyContent: 'center',
											color: 'white',
											width: '13rem',
										}}
									>
										Sample CSV
									</Button>
									<Upload
										accept={'.csv'}
										beforeUpload={beforeUpload}
										showUploadList={false}
										disabled={importLoading}
									>
										<Button
											size="small"
											icon={<DownloadOutlined />}
											loading={importLoading}
											disabled={importLoading}
											className={styles['headerButtons']}
											style={{
												backgroundColor: importLoading ? '#ccc' : '#344735',
												fontSize: '12px',
												borderRadius: '12px',
												height: '41px',
												display: 'inline-flex',
												alignItems: 'center',
												justifyContent: 'center',
												color: 'white',
												width: '13rem',
											}}
										>
											{importLoading ? 'Importing...' : 'Import'}
										</Button>
									</Upload>
								</Space>
							</div>
							{uploading && (
								<div style={{ marginBottom: 8 }}>
									<Progress
										percent={uploadProgress}
										size="small"
										status={uploadProgress === 100 ? 'success' : 'active'}
										format={(percent) => {
											if (percent === 0) return 'Parsing CSV...';
											if (percent === 50) return 'Validating data...';
											if (percent === 75) return 'Populating table...';
											if (percent === 100) return 'Import complete!';
											return `${percent}%`;
										}}
									/>
								</div>
							)}
							<Table
								columns={columns}
								dataSource={rows}
								rowKey="id"
								pagination={false}
								className="table-global"
								size="small"
								scroll={{ y: 240, x: true as any }}
							/>
							<div onClick={addRow} className={styles['add-button']}>
								<BlueAddSvg />
								<p className={styles['add-more']}>Add Another Item</p>
							</div>
							{/* Total Percentage Display */}
							<div
								style={{
									marginTop: 12,
									padding: '8px 12px',
									backgroundColor: '#f5f5f5',
									borderRadius: 4,
									fontSize: 14,
									fontWeight: 500,
									textAlign: 'right',
								}}
							>
								Total Percentage:{' '}
                                {(() => {
                                    const total = rows.reduce((sum, r) => sum + (Number(r.percentage) || 0), 0);
                                    // Round to 2 decimal places, and if very close to 100, show 100
                                    const rounded = Math.round(total * 100) / 100;
                                    return Math.abs(rounded - 100) < 0.01 ? 100 : rounded.toFixed(2);
                                })()}%
                            </div>
							{validationError && csvErrors.length === 0 && (
								<div style={{ color: '#cf1322', fontSize: 12, marginTop: 6 }}>
									{validationError}
								</div>
							)}
							{csvErrors.length > 0 && (
								<div style={{ color: '#cf1322', fontSize: 12, marginTop: 6 }}>
									{csvErrors.map((e, i) => (
										<div key={i}>{e}</div>
									))}
								</div>
							)}
						</div>

						<div className={styles['modal__footer']}>
							<div>
								<Button className={styles['reset']} onClick={handleReset}>
									Reset
								</Button>
							</div>
							<div style={{ display: 'flex', gap: 16 }}>
								<Button className={styles['cancel']} onClick={handleCancel}>
									Cancel
								</Button>
								<Button
									className={`${styles['save']} ${proceedLoading && 'pointer-event-none'
										}`}
									onClick={handleProceed}
									disabled={
										!!validationError ||
										csvErrors.length > 0 ||
										proceedLoading ||
										(singleAccountOnly && selectedAccounts.length === 0)
									}
								>
									{proceedLoading ? (
										<img src="/assets/gifs/loading-black.gif" height={40} />
									) : (
										'Proceed with Allocation'
									)}
								</Button>
							</div>
						</div>
					</div>
				</Spin>
			</Modal>
		</>
	);
};

export default ExpenseGroupSettingsModal;
